/**
 * The session indicator's rules (Flow direction §2.1 step 7, §2.2): what the pill and its live card say, as
 * pure functions, so the component holds only wiring and the spec needs no TestBed.
 */
import type { FlowLiveHead, FlowLiveRecording, FlowLiveWhere } from '../../../../core/services/flow-live-session.store';
import { formatElapsed } from '../../../flow-v2/live/live-clock';

/** The popover band's key for the live card: the `data-popover` value and the key its close is filed under. */
export const FLOW_SESSION_CARD_KEY = 'shell.flow-session-card';

/** A mouse resting on the pill this long opens the card; leaving the pill and the card closes it after the second, ms. */
export const HOVER_OPEN_MS = 350;
export const HOVER_CLOSE_MS = 300;

/** How many other people's heads the pill shows; the card shows everyone, up to `CARD_HEADS`. */
export const PILL_HEADS = 3;
export const CARD_HEADS = 8;

/** The pill's heads: the others, present first (the store's order), at most three. Me is in the card. */
export function pillHeads(heads: readonly FlowLiveHead[]): readonly FlowLiveHead[] {
  return heads.filter((head) => !head.me).slice(0, PILL_HEADS);
}

/** The card's heads, and how many more there are. */
export function cardHeads(heads: readonly FlowLiveHead[]): { readonly shown: readonly FlowLiveHead[]; readonly more: number } {
  return { shown: heads.slice(0, CARD_HEADS), more: Math.max(0, heads.length - CARD_HEADS) };
}

/** The words for where somebody is. */
export function whereKey(where: FlowLiveWhere): string {
  switch (where) {
    case 'NOTES':
      return 'shell.flow_session.where_notes';
    case 'BOARD':
      return 'shell.flow_session.where_board';
    default:
      return 'shell.flow_session.where_away';
  }
}

/** The time as the pill and the card show it, or empty while the store cannot tell. */
export function timeLabel(elapsedMs: number | null): string {
  return elapsedMs === null ? '' : formatElapsed(elapsedMs);
}

/**
 * The card's state chip: "Rec 14:22" while this tab records the session, "Paused 14:22", the words alone while
 * it connects, saves or lost its microphone, and "Live" when nothing records here.
 */
export function stateChip(recording: FlowLiveRecording): { readonly key: string; readonly withTime: boolean; readonly tone: 'rec' | 'quiet' | 'trouble' } {
  switch (recording) {
    case 'recording':
      return { key: 'shell.flow_session.rec', withTime: true, tone: 'rec' };
    case 'paused':
      return { key: 'shell.flow_session.paused', withTime: true, tone: 'quiet' };
    case 'connecting':
      return { key: 'shell.flow_session.connecting', withTime: false, tone: 'quiet' };
    case 'stopping':
      return { key: 'shell.flow_session.stopping', withTime: false, tone: 'quiet' };
    case 'failed':
      return { key: 'shell.flow_session.not_here', withTime: false, tone: 'trouble' };
    default:
      return { key: 'shell.flow_session.live', withTime: false, tone: 'quiet' };
  }
}

/** Pause or Resume is this tab's to press: it records the session here. */
export function canMute(recording: FlowLiveRecording): boolean {
  return recording === 'recording' || recording === 'paused';
}
